import dayjs, { type Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import capitalize from 'lodash/capitalize'

import { TZ } from '../../constants/date.constants'
import { type UserSubscriptionType } from '../../types/SubscriptionType'
import { dateToMonthYear, monthYearToDate } from '../../utilities/date'
import { type UsedEventsMap } from '../useEventActions/useEventActions'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(isBetween)

const getSubscriptionForDate = (subscriptions: UserSubscriptionType[], date: Dayjs) => {
  if (!subscriptions) return null

  // this was previously `const isCurrentMonth = dayjs().isSame(date, 'month')
  // but it didn't behave correctly in some cases
  // see https://github.com/iamkun/dayjs/issues/1189 for discussion
  // accessing month directly seems to work correctly in all cases
  const isCurrentMonth = dayjs().month() === date.month()

  const subscription = subscriptions.find((subscription) => {
    const validFromStartOfMonth = dayjs(subscription.validFrom).tz(TZ).startOf('month')
    if (isCurrentMonth) {
      return !date.isBefore(validFromStartOfMonth) && !subscription.validTo
    } else {
      return subscription.validTo
        ? date.isBetween(validFromStartOfMonth, dayjs(subscription.validTo), 'month', '[]')
        : !date.isBefore(validFromStartOfMonth)
    }
  })
  return subscription || null
}

export const getSubscriptionForMonthYear = (subscriptions: UserSubscriptionType[], monthYear: string) => {
  const date = monthYearToDate(monthYear)
  return getSubscriptionForDate(subscriptions, date)
}

export const getUnusedCarryOverInSubscription = (
  subscription: UserSubscriptionType,
  usedSubscriptionEvents: UsedEventsMap,
) => {
  const pastCarryOvers = subscription.carryOver
  const usedEvents = usedSubscriptionEvents[subscription.id] || {}
  const futureFromCarryOver = Object.values(usedEvents).reduce((usedFromCarryOver, countByMonth) => {
    const overLimit = countByMonth - subscription.noOfSessionsIncluded

    return usedFromCarryOver + (overLimit > 0 ? overLimit : 0)
  }, 0)

  return pastCarryOvers - futureFromCarryOver
}

const getFreeRegularSessionsInSubscription = (
  subscription: UserSubscriptionType,
  usedSubscriptionEvents: UsedEventsMap,
  monthYear: string,
) => {
  const usedSessions = (usedSubscriptionEvents[subscription?.id] || {})[monthYear] || 0
  const diff = subscription.noOfSessionsIncluded - usedSessions
  return diff > 0 ? diff : 0
}

export const getFreeSubscriptionEventsForMonthYear = (
  subscriptions: UserSubscriptionType[],
  usedSubscriptionEvents: UsedEventsMap,
  monthYear: string,
) => {
  const subscription = getSubscriptionForMonthYear(subscriptions, monthYear)
  if (!subscription) return 0

  const freeRegularSessions = getFreeRegularSessionsInSubscription(subscription, usedSubscriptionEvents, monthYear)
  const carryOverLeft = getUnusedCarryOverInSubscription(subscription, usedSubscriptionEvents)

  return freeRegularSessions + carryOverLeft
}

export const getNextAvailableMonth = (
  subscriptions: UserSubscriptionType[],
  usedEvents: UsedEventsMap,
  startDate?: Dayjs,
) => {
  if (!subscriptions?.length) return null

  const currentDate = startDate || dayjs().startOf('month')
  const subscription = getSubscriptionForDate(subscriptions, currentDate)
  const availableSessions = getFreeSubscriptionEventsForMonthYear(
    subscriptions,
    usedEvents,
    dateToMonthYear(currentDate),
  )

  // we need to look at least one month in the future
  if (startDate && !subscription) return null

  return availableSessions
    ? capitalize(currentDate.format('MMMM'))
    : getNextAvailableMonth(subscriptions, usedEvents, currentDate.add(1, 'month'))
}
